/* global google */
import { useRef, useState } from 'react';
import {
    Avatar, Col, Divider, Flex, Row, Space, Tag, Typography,
} from 'antd';
import { PageHeader } from '../PageHeader';
import ItemData from './Item';
import GoogleMapView from '../HybridMap/GoogleMapView';
import { GOOGLE_MAPS_KEY } from '../../const/environments.constants';
import { IBooking } from '../../interfaces/booking.interface';
import {
    buildDisplayStops, countCompletedPickups, countPickups, DisplayStop,
} from '../../utils/multiPicking';
import { RouteStops } from './RouteStops';

const { Title } = Typography;

interface StopPinProps {
    // lat/lng los consume GoogleMapView vía child.props (posiciona el OverlayViewF), no este componente.
    /* eslint-disable react/no-unused-prop-types */
    lat: number;
    lng: number;
    /* eslint-enable react/no-unused-prop-types */
    order: number;
    isDelivery: boolean;
    active: boolean;
    onEnter: () => void;
    onLeave: () => void;
}

const StopPin = ({
    order, isDelivery, active, onEnter, onLeave,
}: StopPinProps) => (
    <Flex
        align="center"
        justify="center"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        style={{
            transform: `translate(-50%, -50%) scale(${active ? 1.25 : 1})`,
            transition: 'all .12s',
            cursor: 'pointer',
        }}
    >
        <Avatar
            size={26}
            style={{
                backgroundColor: isDelivery ? '#eb2f96' : '#00B2E3',
                border: '2px solid #fff',
                boxShadow: active ? '0 0 0 4px rgba(0,178,227,.35)' : '0 1px 4px rgba(0,0,0,.3)',
                fontWeight: 700,
                fontSize: 12,
            }}
        >
            {isDelivery ? '🏁' : order}
        </Avatar>
    </Flex>
);

interface MultiPickingRouteProps {
    booking: IBooking;
}

const MultiPickingRoute = ({ booking }: MultiPickingRouteProps) => {
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const polylineRef = useRef<google.maps.Polyline | null>(null);

    const stops: DisplayStop[] = buildDisplayStops(booking);
    const pickups = countPickups(stops);
    const completed = countCompletedPickups(stops);
    const distance = booking.distanceBreakUp?.exactDistance
        ?? booking.distanceBreakUp?.billedDistance;

    const lats = stops.map((s) => s.lat);
    const lngs = stops.map((s) => s.lng);
    const center = stops.length
        ? {
            lat: (Math.min(...lats) + Math.max(...lats)) / 2,
            lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
        }
        : { lat: 0, lng: 0 };
    const bounds: [number, number, number, number] | null = stops.length
        ? [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)]
        : null;

    const handleLoaded = (map: google.maps.Map) => {
        if (polylineRef.current) polylineRef.current.setMap(null);
        if (stops.length < 2) return;
        polylineRef.current = new google.maps.Polyline({
            path: stops.map((s) => ({ lat: s.lat, lng: s.lng })),
            strokeColor: '#00B2E3',
            strokeOpacity: 0.85,
            strokeWeight: 3,
        });
        polylineRef.current.setMap(map);
    };

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <PageHeader
                title={(
                    <Space size={8} wrap>
                        <Title level={5} style={{ marginBottom: 0 }}>Multi Picking</Title>
                        <Tag color="cyan">{`${pickups} pickups`}</Tag>
                        <Tag>{`${completed} de ${pickups} recogidas`}</Tag>
                        {distance ? <Tag>{`${distance.toFixed(2)} km`}</Tag> : null}
                    </Space>
                )}
                footer={<Divider />}
            />

            <ItemData label="Conductor" value={booking.driverName || 'Sin asignar'} />

            <Row gutter={[16, 16]} style={{ width: '100%' }}>
                <Col xs={24} lg={10}>
                    <RouteStops stops={stops} hoveredId={hoveredId} onHover={setHoveredId} />
                </Col>
                <Col xs={24} lg={14}>
                    <Flex vertical style={{ height: 440, borderRadius: 8, overflow: 'hidden' }}>
                        <GoogleMapView
                            apiKey={GOOGLE_MAPS_KEY}
                            center={center}
                            bounds={bounds}
                            onLoaded={handleLoaded}
                            onLoadError={() => {}}
                        >
                            {stops.map((s) => (
                                <StopPin
                                    key={s.id}
                                    lat={s.lat}
                                    lng={s.lng}
                                    order={s.order}
                                    isDelivery={s.kind === 'DELIVERY'}
                                    active={hoveredId === s.id}
                                    onEnter={() => setHoveredId(s.id)}
                                    onLeave={() => setHoveredId(null)}
                                />
                            ))}
                        </GoogleMapView>
                    </Flex>
                </Col>
            </Row>
        </Space>
    );
};

export { MultiPickingRoute };
